import { APP_PATHS } from "./app-route-paths";
import { MERCHANT_DASHBOARD_ROLES } from "./merchant-dashboard-roles";

/** Default landing route for each Keycloak group. */
export const GROUP_DEFAULT_ROUTE: Record<string, string> = {
  "/MESSAGING": APP_PATHS.dashboard,
  "/ORDER": APP_PATHS.catalog.orders,
  "/PAYMENT": APP_PATHS.payment.links,
  "/DELIVERY": APP_PATHS.catalog.delivery,
  "/VOUCHER": APP_PATHS.catalog.vouchers,
  "/ADMINISTRATION": APP_PATHS.settings.company,
  "/SUPPORT": APP_PATHS.messaging.tickets,
  "/SOLUTION": APP_PATHS.platform.companies,
};

/**
 * Post-login landing priority (first matching group wins).
 * Order: Messaging → Support → Order → Payment → Administration last.
 */
export const GROUP_LANDING_PRIORITY: readonly string[] = [
  "/MESSAGING",
  "/SUPPORT",
  "/ORDER",
  "/PAYMENT",
  "/DELIVERY",
  "/VOUCHER",
  "/ADMINISTRATION",
  "/SOLUTION",
];

/** Roles that unlock each group's default home route in the menu. */
const ROUTE_LANDING_ROLES: Record<string, readonly string[]> = {
  // Any role of a dashboard section (ANALYTICS_CHAT included) opens the merchant dashboard.
  [APP_PATHS.dashboard]: MERCHANT_DASHBOARD_ROLES,
  [APP_PATHS.catalog.orders]: ["ORDERS", "ORDERS_VIEW", "REFERENTIAL_MANAGE"],
  [APP_PATHS.payment.links]: ["PAYMENT_VIEW", "MANAGE_PAYMENT_LINK"],
  [APP_PATHS.catalog.delivery]: ["MERCHANT", "MANAGE_DELIVERYMAN", "PARCELS"],
  [APP_PATHS.catalog.vouchers]: ["MANAGE_VOUCHER", "VOUCHER_VIEW"],
  [APP_PATHS.messaging.tickets]: ["TICKET_VIEW", "MANAGE_TICKET"],
  [APP_PATHS.settings.company]: ["COMPANY_MANAGE", "CATALOG_SUBSCRIPTION"],
  [APP_PATHS.platform.companies]: ["DASHY_COMPANIES", "ADMIN_SOLUTION_UI"],
};

const ROLE_LANDING_PRIORITY: ReadonlyArray<[string, string]> = [
  ["CONVERSATION_VIEW", APP_PATHS.messaging.inbox],
  ["TEAM_INBOX", APP_PATHS.messaging.inbox],
  ["CONTACT_VIEW", APP_PATHS.messaging.contacts],
  ["MANAGE_CONTACT", APP_PATHS.messaging.contacts],
  ["ORDERS_VIEW", APP_PATHS.catalog.orders],
  ["ORDERS", APP_PATHS.catalog.orders],
  ["TICKET_VIEW", APP_PATHS.messaging.tickets],
  ["MANAGE_TICKET", APP_PATHS.messaging.tickets],
  ["PRODUCT_VIEW", APP_PATHS.catalog.products],
  ["PRODUCT", APP_PATHS.catalog.products],
  ["PAYMENT_VIEW", APP_PATHS.payment.links],
  ["MANAGE_PAYMENT_LINK", APP_PATHS.payment.links],
  ["BROADCAST_VIEW", APP_PATHS.campaigns.history],
  ["BROADCAST_HISTORY", APP_PATHS.campaigns.history],
  ["VOUCHER_VIEW", APP_PATHS.catalog.vouchers],
  ["MANAGE_VOUCHER", APP_PATHS.catalog.vouchers],
  ["WORKFLOW_VIEW", APP_PATHS.workflow.templates],
  ["MANAGE_TEMPLATE", APP_PATHS.workflow.templates],
  // Last resort before the hard /not-found fallback, not first choice: every downgraded
  // (view-only) account keeps ANALYTICS_CHAT unconditionally (see backend
  // BusinessRoleAccessService#toViewOnly), so this is what stops a narrowly-configured
  // agent/admin — one whose other roles all dropped out of every entry above — from
  // landing on a dead-end 404 with a broken "return" link instead of their dashboard.
  ["ANALYTICS_CHAT", APP_PATHS.dashboard],
];

function canAccessGroupLandingRoute(
  route: string,
  hasRole?: (role: string) => boolean,
): boolean {
  const requiredRoles = ROUTE_LANDING_ROLES[route];
  if (!requiredRoles?.length) {
    return true;
  }
  return !!hasRole && requiredRoles.some((role) => hasRole(role));
}

/**
 * The last resort is `/not-found`, not the dashboard.
 *
 * The dashboard needs one of `MERCHANT_DASHBOARD_ROLES`, so handing it to a user who matched nothing above sends
 * them to a page whose own guard bounces them straight back here — a redirect loop that shows as a
 * permanently blank screen after login. `/not-found` is ungated, so it terminates.
 */
export function resolveGroupLandingRoute(
  hasGroup: (group: string) => boolean,
  fallback: string = APP_PATHS.notFound,
  hasRole?: (role: string) => boolean,
): string {
  for (const group of GROUP_LANDING_PRIORITY) {
    if (!hasGroup(group)) {
      continue;
    }
    const defaultRoute = GROUP_DEFAULT_ROUTE[group];
    if (defaultRoute && canAccessGroupLandingRoute(defaultRoute, hasRole)) {
      return defaultRoute;
    }
  }

  if (hasRole) {
    const roleTarget = ROLE_LANDING_PRIORITY.find(([role]) => hasRole(role));
    if (roleTarget) {
      return roleTarget[1];
    }
  }

  return fallback;
}
